'use client';

import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AiSkillPublic } from '@ctrl-enter/types/ai-skills';
import { cn } from '../../utils/cn';
import { Icon, type IconProps } from '../icons/icon';

/** Fallback when no skill is selected. */
const DEFAULT_ACCENT = 'from-primary/10 to-primary/10';

const GAP_PX = 32;

/** Two gradient stacks crossfade opacity when the selected skill changes (gradient stops don't tween in CSS). */
function useSkillAccentCrossfade(skill: AiSkillPublic | null) {
  const stableKey = skill?.id ?? '';
  const initialGrad = skill?.accentColor ?? DEFAULT_ACCENT;

  const [a, setA] = useState<{ grad: string; visible: boolean }>({ grad: initialGrad, visible: true });
  const [b, setB] = useState<{ grad: string; visible: boolean }>({ grad: initialGrad, visible: false });
  const prevKeyRef = useRef<string | null>(null);
  const topIsB = useRef(false);
  const skillRef = useRef(skill);
  skillRef.current = skill;

  useLayoutEffect(() => {
    const g = skillRef.current?.accentColor ?? DEFAULT_ACCENT;
    const id = stableKey;

    if (prevKeyRef.current === null) {
      prevKeyRef.current = id;
      setA({ grad: g, visible: true });
      setB({ grad: g, visible: false });
      topIsB.current = false;
      return;
    }

    if (prevKeyRef.current === id) return;
    prevKeyRef.current = id;

    if (topIsB.current) {
      setA({ grad: g, visible: true });
      setB(prev => ({ ...prev, visible: false }));
      topIsB.current = false;
    } else {
      setB({ grad: g, visible: true });
      setA(prev => ({ ...prev, visible: false }));
      topIsB.current = true;
    }
  }, [stableKey]);

  return { a, b };
}

/** Truncated single line with ellipsis; on hover (parent `group`) loops full text like a ticker. */
function SkillMarqueeTitle({ text }: { text: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [needsMarquee, setNeedsMarquee] = useState(false);
  const [durationSec, setDurationSec] = useState(14);
  const [reducedMotion, setReducedMotion] = useState(false);

  useLayoutEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    setReducedMotion(!!mq?.matches);
    const onChange = () => setReducedMotion(!!mq.matches);
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);

  useLayoutEffect(() => {
    const wrap = containerRef.current;
    const measure = measureRef.current;
    if (!wrap || !measure) return;

    const measureAndSet = () => {
      const overflow = measure.scrollWidth > wrap.clientWidth + 2;
      setNeedsMarquee(overflow);
      if (overflow && !reducedMotion) {
        setDurationSec(Math.min(26, Math.max(7, (measure.scrollWidth + GAP_PX) / 32)));
      }
    };

    measureAndSet();
    const ro = new ResizeObserver(measureAndSet);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [text, reducedMotion]);

  return (
    <div
      ref={containerRef}
      className="relative mt-0.5 min-w-0 w-full leading-none"
      title={needsMarquee ? text : undefined}
    >
      <span
        ref={measureRef}
        className="pointer-events-none absolute left-0 top-0 -z-10 max-w-none whitespace-nowrap text-[11px] font-semibold opacity-0"
        aria-hidden
      >
        {text}
      </span>

      {!needsMarquee || reducedMotion ? (
        <span className="block truncate whitespace-nowrap text-[11px] font-semibold leading-none text-foreground">
          {text}
        </span>
      ) : (
        <>
          <span className="block truncate whitespace-nowrap text-[11px] font-semibold leading-none text-foreground group-hover:hidden">
            {text}
          </span>
          <div className="pointer-events-none absolute inset-0 hidden overflow-hidden group-hover:flex items-center">
            <div
              className="skill-sidebar-title-marquee-track min-w-0"
              style={{ '--skill-marquee-duration': `${durationSec}s` } as React.CSSProperties}
            >
              <span className="whitespace-nowrap text-[11px] font-semibold leading-none text-foreground pr-8">{text}</span>
              <span className="whitespace-nowrap text-[11px] font-semibold leading-none text-foreground pr-8" aria-hidden>{text}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

type SkillDynamicIslandProps = {
  skill: AiSkillPublic | null;
  onOpen: () => void;
};

export const SkillDynamicIsland: React.FC<SkillDynamicIslandProps> = ({ skill, onOpen }) => {
  const { a, b } = useSkillAccentCrossfade(skill);
  const [reducedIslandMotion, setReducedIslandMotion] = useState(false);

  useLayoutEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    setReducedIslandMotion(!!mq?.matches);
    const onChange = () => setReducedIslandMotion(!!mq.matches);
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);

  const fadeClass = cn(
    'pointer-events-none absolute inset-0 bg-gradient-to-br transition-opacity ease-out',
    reducedIslandMotion ? 'duration-0' : 'duration-300'
  );

  return (
    <div className="w-full shrink-0 px-2.5 pt-1.5 pb-0.5 animate-in fade-in slide-in-from-top-1 duration-300">
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border shadow-sm',
          skill ? 'border-primary/35' : 'border-primary/20'
        )}
      >
        <div className={cn(fadeClass, a.grad, a.visible ? 'opacity-100' : 'opacity-0')} aria-hidden />
        <div className={cn(fadeClass, b.grad, b.visible ? 'opacity-100' : 'opacity-0')} aria-hidden />

        <button
          type="button"
          onClick={onOpen}
          aria-haspopup="dialog"
          aria-label={skill ? `${skill.name}. Click to change AI specialist` : 'Choose AI specialist'}
          className={cn(
            'relative z-10 flex w-full min-w-0 items-center gap-2 px-2 py-1.5 text-left',
            'hover:bg-transparent active:bg-transparent',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/35'
          )}
        >
          {/* Icon */}
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-background/40 text-foreground">
            {skill ? (
              <Icon iconType={skill.icon as IconProps['iconType']} sizePx={13} />
            ) : (
              <Icon iconType="Stethoscope" sizePx={13} />
            )}
          </div>

          {/* Label stack */}
          <div className="min-w-0 flex-1">
            <span className="block text-[8px] font-semibold uppercase tracking-[0.12em] text-foreground/50 leading-none">
              AI specialist
            </span>
            {skill ? (
              <SkillMarqueeTitle text={skill.name} />
            ) : (
              <span className="mt-0.5 block truncate text-[11px] font-semibold leading-none text-foreground/45">
                Choose a specialist
              </span>
            )}
          </div>

          {/* Chevron */}
          <ChevronDown className="h-3 w-3 shrink-0 text-foreground/40" aria-hidden strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};
